import React, { useEffect, useState } from 'react';
import { alertAPI, sensorAPI } from '../services/api';
import { Alert } from '../types';
import { 
  AlertTriangle, CheckCircle, XCircle, Clock, Settings, Filter, 
  TrendingUp, TrendingDown, Droplets, Activity, Bell,
  Download, Trash2, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import webSocket from '../services/websocket';
import toast from 'react-hot-toast';
import ThresholdSettings from './ThresholdSettings';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface AlertWithContext extends Alert {
  currentValue?: number;
  trend?: 'up' | 'down' | 'stable';
  recommendation?: string;
  priority?: number;
}

const AlertPanelImproved: React.FC = () => {
  const [alerts, setAlerts] = useState<AlertWithContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unacknowledged' | 'critical' | 'warning'>('all');
  const [showSettings, setShowSettings] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);
  const [alertHistory, setAlertHistory] = useState<any[]>([]);
  const [selectedDeviceFilter, setSelectedDeviceFilter] = useState<string>('all');
  const [devices, setDevices] = useState<string[]>([]);
  const [thresholds, setThresholds] = useState<any>(null);

  useEffect(() => {
    fetchAlerts();
    fetchStats();
    fetchAlertHistory();
    fetchDevices();
    fetchThresholds();

    const handleNewAlert = (alert: Alert) => {
      enrichAlertData(alert).then(enrichedAlert => {
        setAlerts(prev => [enrichedAlert, ...prev]);
        fetchStats();
        
        if (alert.severity === 'critical') {
          toast.error(`🚨 ${alert.message}\nCapteur: ${alert.deviceId}`, { duration: 6000 });
        } else {
          toast(`⚠️ ${alert.message}\nCapteur: ${alert.deviceId}`, { duration: 4000 });
        }
      });
    };

    webSocket.on('alert', handleNewAlert);
    return () => webSocket.off('alert', handleNewAlert);
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [filter, selectedDeviceFilter]);

  const fetchDevices = async () => {
    try {
      const response = await sensorAPI.getLatest();
      if (response.data) {
        const deviceIds = response.data.map((s: any) => s.deviceId);
        setDevices(deviceIds);
      }
    } catch (error) {
    }
  };

  const fetchThresholds = async () => {
    try {
      const response = await alertAPI.getThresholds();
      setThresholds(response.data);
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    }
  };

  const enrichAlertData = async (alert: Alert): Promise<AlertWithContext> => {
    try {
      const sensorResponse = await sensorAPI.getLatest();
      const sensorData = sensorResponse.data.find((s: any) => s.deviceId === alert.deviceId);
      const currentValue = sensorData?.sensors?.soilMoisture?.value;
      
      let trend: 'up' | 'down' | 'stable' = 'stable';
      if (currentValue && alert.value) {
        if (currentValue > alert.value + 2) trend = 'up';
        else if (currentValue < alert.value - 2) trend = 'down';
      }

      let recommendation = '';
      if (alert.type === 'low_moisture') {
        recommendation = currentValue && currentValue < 30 
          ? '🚨 Action urgente : Irrigation immédiate recommandée (2-3 L/m²)'
          : '💡 Planifier une irrigation dans les 2-4 prochaines heures';
      } else if (alert.type === 'high_moisture') {
        recommendation = '⚠️ Vérifier le système de drainage et réduire l\'arrosage';
      }

      let priority = 5;
      if (alert.severity === 'critical') priority += 3;
      if (currentValue && alert.threshold) {
        const deviation = Math.abs(currentValue - alert.threshold);
        priority += Math.min(2, Math.floor(deviation / 10));
      }
      if (!alert.acknowledged) priority += 1;

      return { ...alert, currentValue, trend, recommendation, priority };
    } catch (error) {
      return { ...alert, priority: 5 };
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (filter === 'unacknowledged') params.acknowledged = false;
      if (filter === 'critical') params.severity = 'critical';
      if (filter === 'warning') params.severity = 'warning';
      if (selectedDeviceFilter !== 'all') params.deviceId = selectedDeviceFilter;
      
      const response = await alertAPI.getAlerts(params);
      const enrichedAlerts = await Promise.all(
        response.data.alerts.map(enrichAlertData)
      );
      
      enrichedAlerts.sort((a, b) => {
        if (b.priority !== a.priority) return (b.priority || 0) - (a.priority || 0);
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      
      setAlerts(enrichedAlerts);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Échec de récupération des alertes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await alertAPI.getStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchAlertHistory = async () => {
    try {
      const response = await alertAPI.getAlerts({ limit: 50 });
      const alerts = response.data.alerts;
      
      const hourlyData: any = {};
      alerts.forEach((alert: Alert) => {
        const hour = format(new Date(alert.timestamp), 'HH:00', { locale: fr });
        if (!hourlyData[hour]) {
          hourlyData[hour] = { time: hour, critical: 0, warning: 0, info: 0 };
        }
        if (alert.severity === 'critical') hourlyData[hour].critical++;
        else if (alert.severity === 'warning') hourlyData[hour].warning++;
        else hourlyData[hour].info++;
      });

      setAlertHistory(Object.values(hourlyData).slice(-24));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await alertAPI.acknowledgeAlert(alertId);
      setAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId
            ? { ...alert, acknowledged: true, acknowledgedAt: new Date().toISOString() }
            : alert
        )
      );
      toast.success('✅ Alerte acquittée');
      fetchStats();
    } catch (error) {
      toast.error('Échec');
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      const unacknowledged = alerts.filter(a => !a.acknowledged);
      await Promise.all(unacknowledged.map(a => alertAPI.acknowledgeAlert(a.id)));
      setAlerts(prev =>
        prev.map(alert => ({
          ...alert,
          acknowledged: true,
          acknowledgedAt: new Date().toISOString()
        }))
      );
      toast.success(`✅ ${unacknowledged.length} alertes acquittées`);
      fetchStats();
    } catch (error) {
      toast.error('Échec');
    }
  };

  const handleClearAcknowledged = async () => {
    if (!confirm('Supprimer toutes les alertes acquittées ?')) return;
    
    try {
      await alertAPI.clearAcknowledged();
      setAlerts(prev => prev.filter(alert => !alert.acknowledged));
      toast.success('🗑️ Alertes supprimées');
      fetchStats();
    } catch (error) {
      toast.error('Échec');
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Timestamp', 'Capteur', 'Sévérité', 'Type', 'Message', 'Valeur', 'Seuil', 'Acquittée'].join(','),
      ...alerts.map(a => [
        new Date(a.timestamp).toISOString(),
        a.deviceId,
        a.severity,
        a.type,
        `"${a.message}"`,
        a.value,
        a.threshold,
        a.acknowledged ? 'Oui' : 'Non'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `alertes_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('📥 Export réussi');
  };

  const getSeverityIcon = (severity: string, size = 5) => {
    const sizeClass = `h-${size} w-${size}`;
    switch (severity) {
      case 'critical': return <XCircle className={sizeClass} style={{color: '#ef4444'}} />;
      case 'warning': return <AlertTriangle className={sizeClass} style={{color: '#f59e0b'}} />;
      default: return <Info className={sizeClass} style={{color: '#3b82f6'}} />;
    }
  };

  const getPriorityBadge = (priority: number) => {
    if (priority >= 8) return { text: 'Urgent', color: '#dc2626', bg: '#fee2e2' };
    if (priority >= 6) return { text: 'Élevée', color: '#ea580c', bg: '#ffedd5' };
    if (priority >= 4) return { text: 'Moyenne', color: '#d97706', bg: '#fef3c7' };
    return { text: 'Basse', color: '#65a30d', bg: '#ecfccb' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{minHeight: '24rem'}}>
        <div style={{
          width: '3rem', height: '3rem',
          border: '3px solid #e5e7eb',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}></div>
      </div>
    );
  }

  const unacknowledgedCount = alerts.filter(a => !a.acknowledged).length;
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const COLORS = ['#ef4444', '#f59e0b', '#3b82f6'];
  const pieData = [
    { name: 'Critiques', value: criticalCount },
    { name: 'Avertissements', value: alerts.filter(a => a.severity === 'warning').length },
    { name: 'Info', value: alerts.filter(a => a.severity === 'info').length }
  ].filter(d => d.value > 0);

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
      {/* Enhanced Stats with Gradients */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <div>
                <p style={{fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem'}}>Total</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', margin: 0}}>{stats?.total || 0}</p>
                <p style={{fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem'}}>Dernières 24h</p>
              </div>
              <Bell style={{width: '2rem', height: '2rem', opacity: 0.8}} />
            </div>
          </div>

          <div className="card" style={{background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <div>
                <p style={{fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem'}}>Critiques</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', margin: 0}}>{criticalCount}</p>
                <p style={{fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem'}}>
                  {criticalCount > 0 ? 'Action requise' : 'Aucune'}
                </p>
              </div>
              <XCircle style={{width: '2rem', height: '2rem', opacity: 0.8}} />
            </div>
          </div>

          <div className="card" style={{background: 'linear-gradient(135deg, #fad961 0%, #f76b1c 100%)', color: 'white'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <div>
                <p style={{fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem'}}>Avertissements</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', margin: 0}}>{stats?.warning || 0}</p>
                <p style={{fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem'}}>À surveiller</p>
              </div>
              <AlertTriangle style={{width: '2rem', height: '2rem', opacity: 0.8}} />
            </div>
          </div>

          <div className="card" style={{background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <div>
                <p style={{fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem'}}>Non acquittées</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', margin: 0}}>{unacknowledgedCount}</p>
                <p style={{fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem'}}>
                  {unacknowledgedCount > 0 ? 'En attente' : 'Toutes traitées'}
                </p>
              </div>
              <Clock style={{width: '2rem', height: '2rem', opacity: 0.8}} />
            </div>
          </div>
        </div>

        {pieData.length > 0 && (
          <div className="card">
            <h3 className="card-title" style={{marginBottom: '1rem', fontSize: '0.9rem'}}>
              📊 Distribution
            </h3>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div style={{display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem', fontSize: '0.75rem'}}>
              {pieData.map((entry, index) => (
                <div key={entry.name} style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                  <div style={{width: '0.75rem', height: '0.75rem', backgroundColor: COLORS[index], borderRadius: '2px'}}></div>
                  <span style={{color: 'var(--text-secondary)'}}>{entry.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timeline Historique */}
      {alertHistory.length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{marginBottom: '1rem'}}>📈 Historique (24h)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={alertHistory}>
              <defs>
                <linearGradient id="critical" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="warning" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="time" style={{fontSize: '0.75rem'}} />
              <YAxis style={{fontSize: '0.75rem'}} />
              <Tooltip contentStyle={{backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)'}} />
              <Area type="monotone" dataKey="critical" stroke="#ef4444" fillOpacity={1} fill="url(#critical)" name="Critiques" />
              <Area type="monotone" dataKey="warning" stroke="#f59e0b" fillOpacity={1} fill="url(#warning)" name="Avertissements" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filtres et Actions */}
      <div className="card">
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between'}}>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center'}}>
            <Filter style={{width: '1.25rem', height: '1.25rem', color: 'var(--text-secondary)'}} />
            
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-light)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">Toutes ({alerts.length})</option>
              <option value="unacknowledged">Non acquittées ({unacknowledgedCount})</option>
              <option value="critical">Critiques ({criticalCount})</option>
              <option value="warning">Avertissements ({stats?.warning || 0})</option>
            </select>

            <select
              value={selectedDeviceFilter}
              onChange={(e) => setSelectedDeviceFilter(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-light)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tous les capteurs</option>
              {devices.map(device => (
                <option key={device} value={device}>{device}</option>
              ))}
            </select>
          </div>

          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
            {unacknowledgedCount > 0 && (
              <button onClick={handleAcknowledgeAll} className="btn-primary" style={{fontSize: '0.875rem'}}>
                <CheckCircle style={{width: '1rem', height: '1rem'}} />
                <span>Acquitter tout ({unacknowledgedCount})</span>
              </button>
            )}
            
            <button 
              onClick={() => setShowSettings(true)} 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                backgroundColor: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.9375rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <Settings style={{width: '1.125rem', height: '1.125rem'}} />
              <span>Configurer Seuils</span>
            </button>
            
            <button 
              onClick={handleExport}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.9375rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <Download style={{width: '1.125rem', height: '1.125rem'}} />
              <span>Exporter CSV</span>
            </button>
            
            {alerts.some(a => a.acknowledged) && (
              <button onClick={handleClearAcknowledged} className="btn-danger" style={{fontSize: '0.875rem'}}>
                <Trash2 style={{width: '1rem', height: '1rem'}} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Seuils Configurés */}
      {thresholds && (
        <div className="card">
          <h3 className="card-title" style={{marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <Activity style={{width: '1.25rem', height: '1.25rem', color: '#6366f1'}} />
            Surveillance Active
          </h3>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem'}}>
            <div style={{
              padding: '1rem',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              border: '2px solid #ef4444'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <TrendingDown style={{width: '1.25rem', height: '1.25rem', color: '#dc2626'}} />
                <span style={{fontWeight: '600', color: '#991b1b'}}>Humidité Basse</span>
              </div>
              <p style={{fontSize: '0.875rem', color: '#7f1d1d', margin: 0}}>
                🚨 Alerte si <strong>&lt; {thresholds.moistureMin}%</strong>
              </p>
              <p style={{fontSize: '0.75rem', color: '#991b1b', marginTop: '0.25rem'}}>
                Action: Irrigation immédiate
              </p>
            </div>
            
            <div style={{
              padding: '1rem',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
              border: '2px solid #3b82f6'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <TrendingUp style={{width: '1.25rem', height: '1.25rem', color: '#1d4ed8'}} />
                <span style={{fontWeight: '600', color: '#1e40af'}}>Humidité Élevée</span>
              </div>
              <p style={{fontSize: '0.875rem', color: '#1e3a8a', margin: 0}}>
                💧 Alerte si <strong>&gt; {thresholds.moistureMax}%</strong>
              </p>
              <p style={{fontSize: '0.75rem', color: '#1e40af', marginTop: '0.25rem'}}>
                Action: Vérifier drainage
              </p>
            </div>
            
            <div style={{
              padding: '1rem',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '2px solid #f59e0b'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <Clock style={{width: '1.25rem', height: '1.25rem', color: '#d97706'}} />
                <span style={{fontWeight: '600', color: '#92400e'}}>Cooldown</span>
              </div>
              <p style={{fontSize: '0.875rem', color: '#78350f', margin: 0}}>
                ⏱️ <strong>{thresholds.cooldownMinutes} minutes</strong>
              </p>
              <p style={{fontSize: '0.75rem', color: '#92400e', marginTop: '0.25rem'}}>
                Entre alertes similaires
              </p>
            </div>
          </div>
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            backgroundColor: '#f0fdf4',
            border: '1px solid #86efac',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <CheckCircle style={{width: '1.125rem', height: '1.125rem', color: '#16a34a'}} />
            <span style={{fontSize: '0.875rem', color: '#166534', fontWeight: '500'}}>
              Surveillance automatique active • Notifications en temps réel
            </span>
          </div>
        </div>
      )}

      {/* Liste des Alertes */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
        {alerts.length === 0 ? (
          <div className="card" style={{textAlign: 'center', padding: '3rem'}}>
            <CheckCircle style={{width: '4rem', height: '4rem', color: '#10b981', margin: '0 auto 1rem'}} />
            <h3 style={{color: 'var(--text-primary)', marginBottom: '0.5rem'}}>
              ✨ Aucune alerte active
            </h3>
            <p style={{color: 'var(--text-secondary)'}}>
              Tous les capteurs fonctionnent dans les paramètres normaux
            </p>
          </div>
        ) : (
          alerts.map((alert) => {
            const isExpanded = expandedAlert === alert.id;
            const priorityBadge = getPriorityBadge(alert.priority || 5);
            
            return (
              <div
                key={alert.id}
                className="card"
                style={{
                  borderLeft: `4px solid ${alert.severity === 'critical' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6'}`,
                  opacity: alert.acknowledged ? 0.7 : 1,
                  transition: 'all 0.3s ease'
                }}
              >
                <div onClick={() => setExpandedAlert(isExpanded ? null : alert.id)} style={{cursor: 'pointer'}}>
                  <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: isExpanded ? '1rem' : 0}}>
                    <div style={{padding: '0.5rem', borderRadius: '0.5rem', backgroundColor: alert.severity === 'critical' ? '#fee2e2' : '#fef3c7'}}>
                      {getSeverityIcon(alert.severity, 6)}
                    </div>

                    <div style={{flex: 1}}>
                      <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.5rem'}}>
                        <div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem'}}>
                            <h4 style={{margin: 0, color: 'var(--text-primary)', fontWeight: '600'}}>
                              {alert.message}
                            </h4>
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              borderRadius: '9999px',
                              backgroundColor: priorityBadge.bg,
                              color: priorityBadge.color,
                              fontWeight: '500'
                            }}>
                              {priorityBadge.text}
                            </span>
                          </div>
                          <p style={{margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)'}}>
                            📡 {alert.deviceId} • {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true, locale: fr })}
                          </p>
                        </div>

                        {!alert.acknowledged ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcknowledge(alert.id);
                            }}
                            className="btn-success"
                            style={{fontSize: '0.875rem', whiteSpace: 'nowrap'}}
                          >
                            <CheckCircle style={{width: '1rem', height: '1rem'}} />
                            <span>Acquitter</span>
                          </button>
                        ) : (
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontSize: '0.875rem'}}>
                            <CheckCircle style={{width: '1rem', height: '1rem'}} />
                            <span>Acquittée</span>
                          </div>
                        )}
                      </div>

                      <div style={{display: 'flex', gap: '1.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.75rem'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                          <Droplets style={{width: '1rem', height: '1rem'}} />
                          <span>Valeur: <strong>{alert.value}%</strong></span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                          <Activity style={{width: '1rem', height: '1rem'}} />
                          <span>Seuil: <strong>{alert.threshold}%</strong></span>
                        </div>
                        {alert.currentValue !== undefined && (
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                            {alert.trend === 'up' ? <TrendingUp style={{width: '1rem', height: '1rem', color: '#10b981'}} /> : 
                             alert.trend === 'down' ? <TrendingDown style={{width: '1rem', height: '1rem', color: '#ef4444'}} /> :
                             <Activity style={{width: '1rem', height: '1rem'}} />}
                            <span>Actuel: <strong>{alert.currentValue}%</strong></span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{padding: '0.5rem'}}>
                      {isExpanded ? 
                        <ChevronUp style={{width: '1.25rem', height: '1.25rem', color: 'var(--text-secondary)'}} /> :
                        <ChevronDown style={{width: '1.25rem', height: '1.25rem', color: 'var(--text-secondary)'}} />
                      }
                    </div>
                  </div>

                  {isExpanded && alert.recommendation && (
                    <div style={{
                      padding: '1rem',
                      borderRadius: '0.5rem',
                      backgroundColor: 'var(--bg-secondary)',
                      marginTop: '1rem',
                      borderLeft: '3px solid #3b82f6'
                    }}>
                      <div style={{display: 'flex', alignItems: 'flex-start', gap: '0.75rem'}}>
                        <Info style={{width: '1.25rem', height: '1.25rem', color: '#3b82f6', flexShrink: 0, marginTop: '0.125rem'}} />
                        <div>
                          <h5 style={{margin: 0, marginBottom: '0.25rem', color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: '600'}}>
                            💡 Recommandation
                          </h5>
                          <p style={{margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5'}}>
                            {alert.recommendation}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {showSettings && (
        <ThresholdSettings 
          onClose={() => {
            setShowSettings(false);
            fetchThresholds(); // Recharger les seuils après sauvegarde
          }} 
        />
      )}
    </div>
  );
};

export default AlertPanelImproved;
